/**
 * Default color palette for generating consistent colors.
 *
 * A curated set of colors used throughout the application for
 * generating consistent visual elements like text colors, backgrounds, etc.
 */
export const palette = [
  '#53c79f',
  '#e58c72',
  '#7a6fca',
  '#ca6f96',
  '#64b0cc',
  '#e5c072',
] as const

/**
 * Color palette for input components and user selections.
 *
 * Extended color set including common colors like white, black,
 * and various vibrant colors for user interface elements.
 * Colors are sorted by hue for better visual organization.
 */
export const inputColors = [
  // '#FFFFFF', // White
  '#000000', // Black
  '#ff9869', // disco-latin
  '#ff45ca', // disco-pop
  '#d095ff', // disco-soul
  '#4e98ff', // disco-jazz
  '#37ff3e', // disco-techno
  // '#e3ffe9', // disco-rave
  // '#ede8dd', // disco-wheatpaste
  '#fffc52', // disco-yellow
  '#910460', // disco-classical
  '#3d5b01', // disco-folk
] as const

/**
 * Generates a consistent color based on a name string.
 *
 * Uses character codes from the name to deterministically select
 * a color from the palette. Same name will always return the same color.
 * Useful for user avatars, labels, or any element that needs consistent
 * color assignment based on text.
 *
 * @param name - The name string to generate a color for
 * @returns Hex color string from the palette
 *
 * @example
 * ```typescript
 * const color1 = getTextColor('John Doe')
 * const color2 = getTextColor('John Doe')
 * // color1 === color2 (always the same for same input)
 *
 * const color3 = getTextColor('Jane Smith')
 * // Different color based on different character codes
 *
 * const color4 = getTextColor(null)
 * // Returns first palette color for null/undefined
 * ```
 */
export const getTextColor = (name?: string | null): string => {
  let code = 0
  if (name) {
    name.split('').forEach(char => (code += char.charCodeAt(0)))
  }
  const index = code % 10 > palette.length - 1 ? palette.length - 1 : code % 10
  return palette[index] || palette[0]
}
